import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const STATUS_CFG = {
  active:          { label: 'Activo',       color: '#1A7F4E', bg: '#EAF7F0', border: '#A3D9BF' },
  configured_only: { label: 'Sin proceso',  color: '#8C6B00', bg: '#FFF8E1', border: '#FFD54F' },
  none:            { label: 'Sin backup',   color: '#B0291D', bg: 'var(--red-dim)', border: '#F5C0BC' },
};

const SERVICE_ICONS = {
  'OneDrive':     '☁',
  'Google Drive': '📁',
  'Dropbox':      '📦',
};

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: 'var(--gray-0)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '14px 18px' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.none;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {cfg.label}
    </span>
  );
}

export default function CloudSync() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/cloud-sync/summary`, { headers })
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Cargando...</div>;
  if (!data)   return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Error al cargar datos.</div>;

  const { summary, assets } = data;

  const filtered = (assets || []).filter(a => {
    const matchSearch = !search ||
      a.hostname?.toLowerCase().includes(search.toLowerCase()) ||
      a.user_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === 'all'      ? true :
      filter === 'none'     ? a.status === 'none' :
      filter === 'active'   ? a.status === 'active' :
      filter === 'personal' ? a.has_personal : true;
    return matchSearch && matchFilter;
  });

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Backup en la nube</div>
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
          OneDrive · Google Drive · Dropbox — sincronización por equipo
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total equipos"          value={summary.total_assets}      color="var(--gray-700)" />
        <StatCard label="Sync activo"             value={summary.active}            color="#1A7F4E" />
        <StatCard label="Sin sync activo"         value={summary.configured_only}   color="#8C6B00" />
        <StatCard label="Sin ningún backup"       value={summary.no_sync}           color="#B0291D" />
      </div>

      {summary.personal_accounts > 0 && (
        <div style={{ background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 8,
          padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#8C6B00', display: 'flex', gap: 8 }}>
          <strong>⚠ Cuentas personales detectadas:</strong>
          {summary.personal_accounts} equipo(s) con datos sincronizados a cuentas no corporativas.
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Buscar equipo o usuario..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '6px 12px', border: '1px solid var(--gray-300)', borderRadius: 6,
            fontSize: 13, width: 240, background: 'var(--gray-0)', color: 'var(--gray-900)' }}
        />
        {[
          { key: 'all',      label: 'Todos' },
          { key: 'active',   label: '🟢 Activo' },
          { key: 'none',     label: '🔴 Sin backup' },
          { key: 'personal', label: '⚠ Cuenta personal' },
        ].map(f => (
          <button key={f.key}
            className={`btn ${filter === f.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(f.key)}
            style={{ fontSize: 12 }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              {['Equipo', 'Usuario', 'Estado', 'Servicios', 'Carpetas sincronizadas', 'Último sync'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                  fontSize: 11, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>
                Sin resultados
              </td></tr>
            ) : filtered.map(asset => (
              <tr key={asset.asset_id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--gray-900)' }}>
                  {asset.hostname}
                  {asset.has_personal && (
                    <span title="Cuenta personal detectada"
                      style={{ marginLeft: 6, fontSize: 10, color: '#8C6B00', background: '#FFF8E1',
                        border: '1px solid #FFD54F', borderRadius: 3, padding: '1px 4px' }}>
                      personal
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--gray-600)' }}>{asset.user_name || '—'}</td>
                <td style={{ padding: '10px 14px' }}><StatusBadge status={asset.status} /></td>
                <td style={{ padding: '10px 14px' }}>
                  {asset.services.length === 0 ? (
                    <span style={{ color: 'var(--gray-400)' }}>—</span>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {asset.services.map((s, i) => (
                        <span key={i} title={s.email || s.service}
                          style={{ background: s.process_active ? '#EAF7F0' : 'var(--gray-100)',
                            color: s.process_active ? '#1A7F4E' : 'var(--gray-600)',
                            border: `1px solid ${s.process_active ? '#A3D9BF' : 'var(--gray-200)'}`,
                            borderRadius: 4, padding: '2px 7px', fontSize: 11, whiteSpace: 'nowrap' }}>
                          {SERVICE_ICONS[s.service] || '☁'} {s.service}
                          {s.email && <span style={{ opacity: 0.7 }}> · {s.email.split('@')[0]}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--gray-600)', maxWidth: 260 }}>
                  {asset.services.flatMap(s => {
                    const folders = [];
                    if (s.sync_folder) folders.push(s.sync_folder);
                    if (s.synced_folders) {
                      try {
                        const sf = typeof s.synced_folders === 'string'
                          ? JSON.parse(s.synced_folders) : s.synced_folders;
                        sf.forEach(f => f.path && folders.push(f.path));
                      } catch {}
                    }
                    return folders;
                  }).filter((v, i, a) => a.indexOf(v) === i).slice(0, 3).map((f, i) => (
                    <div key={i} style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis' }} title={f}>
                      {f}
                    </div>
                  )) || <span style={{ color: 'var(--gray-400)' }}>—</span>}
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--gray-500)', fontSize: 11 }}>
                  {asset.services.find(s => s.last_sync_log)
                    ? new Date(asset.services.find(s => s.last_sync_log).last_sync_log)
                        .toLocaleDateString('es-AR')
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
